import { useEffect, useMemo, useRef, useState } from 'react';
import { useDynamicExperienceContext } from '@de/providers/DynamicExperienceProvider';
import { fetchDynamicOptions } from '@de/utils/data-fetch/fetchDynamicOptions';
import { generateCacheKey } from '@de/utils/data-fetch/prefetchDynamicOptions';
import { flattenStepsForDynamicOptions } from '@de/utils/flattenStepsForDynamicOptions';
import { StepType } from '@de/types/enums';
import type { Option, Question, Step } from '@de/types/types';

export interface UseStandaloneDynamicOptionsReturn {
  /** `undefined` signals the caller to keep the step's static `options`. */
  options?: Option[];
  /**
   * Full fetched option set when `dynamicOptions.searchBarOptions` is on and a
   * `limit` is configured. `options` holds the first `limit` for inline
   * display; static-choice variants derive the searchable remainder from this.
   * `undefined` when the search bar is off or no `limit` is set.
   */
  searchBarAllOptions?: Option[];
  isOptionsLoading: boolean;
  dynamicOptionsError?: string;
}

interface FallbackState {
  options?: Option[];
  isLoading: boolean;
  error?: string;
}

const INITIAL_FALLBACK: FallbackState = {
  options: undefined,
  isLoading: false,
  error: undefined,
};

/**
 * Delivers prefetched custom-endpoint options to a standalone (non-MULTI_QUESTION)
 * dynamic question. Cache-first: reads the shared prefetch cache, shows a loading
 * state while the prefetch pass is in flight, and only falls back to a direct
 * fetch on a settled cache miss (e.g. prefetch disabled, ineligible, or errored).
 *
 * No-op for steps that are not a `QUESTION` or that carry no
 * `dynamicOptions.endpointUrl`, leaving the step's static options untouched.
 *
 * When `dynamicOptions.searchBarOptions` is on, the cache (which stores the
 * limited result set) is skipped and options are fetched without a `limit`. If
 * a `limit` is configured, the first `limit` are returned as `options` for
 * inline display and the full set as `searchBarAllOptions` for the search bar;
 * with no `limit`, the full set is returned as `options` and no search bar.
 */
export const useStandaloneDynamicOptions = (
  step: Step,
): UseStandaloneDynamicOptionsReturn => {
  const { getCachedOptions, isPrefetching, flowState, steps } =
    useDynamicExperienceContext();

  // Match the prefetch/cache layer, which keys and resolves dependencies against a
  // flattened list so nested MULTI_QUESTION `stepQuestions` are visible to
  // `getOptionByCustomField`. Using the raw `steps` here would diverge from
  // `getCachedOptions` and mis-resolve query params for endpoints that depend on a
  // nested question's `customField`.
  const flattenedSteps = useMemo(
    () => flattenStepsForDynamicOptions(steps),
    [steps],
  );

  const dynamicOptions =
    step.type === StepType.QUESTION
      ? (step as Question).dynamicOptions
      : undefined;
  const hasDynamicEndpoint = Boolean(dynamicOptions?.endpointUrl);
  const hasSearchBar = dynamicOptions?.searchBarOptions === true;

  // With the search bar on, the shared cache/prefetch (limited results) is
  // bypassed so we can fetch the full option set below.
  const cachedOptions =
    hasDynamicEndpoint && !hasSearchBar ? getCachedOptions(step) : undefined;
  const hasCachedOptions = cachedOptions !== undefined;

  const dependencyKey = useMemo(
    () =>
      hasDynamicEndpoint
        ? generateCacheKey(step, flowState, flattenedSteps)
        : '',
    [hasDynamicEndpoint, step, flowState, flattenedSteps],
  );

  const [fallback, setFallback] = useState<FallbackState>(INITIAL_FALLBACK);

  const stepRef = useRef(step);
  stepRef.current = step;
  const dynamicOptionsRef = useRef(dynamicOptions);
  dynamicOptionsRef.current = dynamicOptions;
  const latestInputsRef = useRef({ flowState, steps: flattenedSteps });
  latestInputsRef.current = { flowState, steps: flattenedSteps };

  /** Dedupes concurrent fetches; the settled ref prevents re-fetch loops on re-render. */
  const inFlightKeyRef = useRef<string | undefined>(undefined);
  const settledKeyRef = useRef<string | undefined>(undefined);

  // Unmount-only guard. We must NOT discard a resolved fetch just because the
  // effect re-ran (e.g. the `isPrefetching` flag flipping while a search-bar
  // fetch — which bypasses the cache — is already in flight). Discarding on a
  // same-key re-run would leave the step stuck in its loading state with the
  // successful response thrown away. Stale results (dependency key changed
  // mid-fetch) are still dropped by the `latestKey !== dependencyKey` check.
  //
  // The ref is reset to `false` on (re)mount so React StrictMode's
  // mount→unmount→remount cycle (which fires the cleanup on the still-mounted
  // instance) can't leave it stuck `true` and silently drop every fetch.
  const isUnmountedRef = useRef(false);
  useEffect(() => {
    isUnmountedRef.current = false;
    return () => {
      isUnmountedRef.current = true;
    };
  }, []);

  useEffect(() => {
    const activeDynamicOptions = dynamicOptionsRef.current;

    if (!hasDynamicEndpoint || !activeDynamicOptions) {
      setFallback(INITIAL_FALLBACK);
      inFlightKeyRef.current = undefined;
      settledKeyRef.current = undefined;
      return;
    }

    const searchBarEnabled = activeDynamicOptions.searchBarOptions === true;

    // The shared cache will (or already does) supply options; no direct fetch
    // needed. Skipped when the search bar is on — that path always fetches the
    // full (unlimited) set directly instead of using the limited cache.
    if (!searchBarEnabled && (hasCachedOptions || isPrefetching)) {
      return;
    }

    if (
      settledKeyRef.current === dependencyKey ||
      inFlightKeyRef.current === dependencyKey
    ) {
      return;
    }
    inFlightKeyRef.current = dependencyKey;

    setFallback((prev) => ({ ...prev, isLoading: true, error: undefined }));

    void (async () => {
      try {
        const { flowState: fs, steps: st } = latestInputsRef.current;
        const fetchConfig = searchBarEnabled
          ? { ...activeDynamicOptions, limit: undefined }
          : activeDynamicOptions;
        const fetched = await fetchDynamicOptions(fetchConfig, fs, st);

        if (isUnmountedRef.current) {
          return;
        }

        const { flowState: fsAfter, steps: stAfter } = latestInputsRef.current;
        const latestKey = generateCacheKey(stepRef.current, fsAfter, stAfter);
        if (latestKey !== dependencyKey) {
          // Dependency changed mid-fetch; a newer effect owns the result.
          return;
        }

        settledKeyRef.current = dependencyKey;
        setFallback({ options: fetched, isLoading: false, error: undefined });
      } catch (err) {
        if (isUnmountedRef.current) {
          return;
        }

        const { flowState: fsAfter, steps: stAfter } = latestInputsRef.current;
        const latestKey = generateCacheKey(stepRef.current, fsAfter, stAfter);
        if (latestKey !== dependencyKey) {
          return;
        }

        settledKeyRef.current = dependencyKey;
        setFallback({
          options: undefined,
          isLoading: false,
          error: err instanceof Error ? err.message : String(err),
        });
      } finally {
        if (inFlightKeyRef.current === dependencyKey) {
          inFlightKeyRef.current = undefined;
        }
      }
    })();
  }, [
    hasDynamicEndpoint,
    hasSearchBar,
    hasCachedOptions,
    isPrefetching,
    dependencyKey,
  ]);

  if (!hasDynamicEndpoint) {
    return { options: undefined, isOptionsLoading: false };
  }

  // Search bar on: always driven by the unlimited fallback fetch (cache/prefetch
  // are bypassed above). Split the full set into inline + searchable when a
  // limit is configured; otherwise return the full set with no search bar.
  if (hasSearchBar) {
    const limit = dynamicOptions?.limit;
    const fetched = fallback.options;
    if (fetched && limit) {
      return {
        options: fetched.slice(0, limit),
        searchBarAllOptions: fetched,
        isOptionsLoading: fallback.isLoading,
        dynamicOptionsError: fallback.error,
      };
    }

    return {
      options: fetched,
      isOptionsLoading: fallback.isLoading,
      dynamicOptionsError: fallback.error,
    };
  }

  if (hasCachedOptions) {
    return { options: cachedOptions, isOptionsLoading: false };
  }

  if (isPrefetching) {
    return { options: undefined, isOptionsLoading: true };
  }

  return {
    options: fallback.options,
    isOptionsLoading: fallback.isLoading,
    dynamicOptionsError: fallback.error,
  };
};
